import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  calculateLessonRewardTotal,
  buildLessonKey,
  formatNims
} from './utils/nims';
import { findDay, getWeeksForCourse } from './data/lessons';
import { useLanguage } from '../context/LanguageContext';

const STORAGE_KEY = 'nimny_lab_state_v1';
const NIM_STORAGE_KEY = 'nimnyLab:nimRewards:v1';

// Account display + role table — must mirror backend lab_auth.py.
// Used only by the demo-mode quick-login buttons. In production the user
// types their login and password into the real form.
const DEMO_LOGIN_CHIPS = [
  { login: 'parent', labelRu: 'Родитель', labelEn: 'Parent' },
  { login: 'max',    labelRu: 'Макс',     labelEn: 'Max' },
  { login: 'anita',  labelRu: 'Анита',    labelEn: 'Anita' }
];
export const isLabDemoMode = () =>
  String(process.env.REACT_APP_LAB_DEMO_MODE || '').toLowerCase() === 'true';
export { DEMO_LOGIN_CHIPS };

// All authenticated child accounts are now backend-mode (Part C). The
// legacy `/lab/max` and `/lab/anita` routes still resolve to the same
// pages, but progress + nims live in MongoDB regardless of who the
// student is. `studentBase` keeps returning the legacy URL prefix for
// `max` / `anita` so existing bookmarks remain stable; the dynamic
// `/lab/student/<sid>/*` routes are also mounted as aliases.
const LEGACY_STUDENT_IDS = ['max', 'anita'];
export const isLegacyStudent = (sid) => LEGACY_STUDENT_IDS.includes(sid);
export const studentBase = (sid) =>
  isLegacyStudent(sid) ? `/lab/${sid}` : `/lab/student/${sid}`;
export const isBackendStudent = (user) =>
  !!(user && user.role === 'child' && user.studentId);

// Detect a legacy student slug ('max' / 'anita') from the current URL.
// Used by every page/component that may render under a legacy route
// (where `useParams().studentId` is undefined) so a parent previewing
// /lab/anita/... never falls back to the wrong child.
export const legacyStudentFromPathname = (pathname) => {
  if (typeof pathname !== 'string') return null;
  const m = pathname.match(/^\/lab\/(max|anita)(\/|$|\?|#)/);
  return m ? m[1] : null;
};

// Single source of truth for the active childId on every Lab page.
// Priority (high → low):
//   1. forcedStudentId — explicit prop pass from LabApp (preferred)
//   2. routeStudentId — dynamic /lab/student/:studentId/... param
//   3. legacy slug detected from pathname: /lab/max/... or /lab/anita/...
//   4. user.studentId — current child's own session
//   5. 'max' as a last-resort fallback
//
// Critically, for parent / super_admin previewing a legacy route, the
// route slug takes precedence over the (null) parent studentId so the
// page renders the child whose URL is being viewed.
export const resolveLabChildId = ({
  forcedStudentId,
  routeStudentId,
  pathname,
  user,
} = {}) => {
  if (forcedStudentId) return forcedStudentId;
  if (routeStudentId) return routeStudentId;
  const legacy = legacyStudentFromPathname(pathname);
  if (legacy) return legacy;
  if (user && user.studentId) return user.studentId;
  return 'max';
};

// Resolve which curriculum (course template) a given child should see.
// Phase 0 of the parallel Anita branch — keeps Max behaviour unchanged for
// every existing user and prevents Anita from accidentally inheriting Max
// content when `courseTemplate` is missing on the backend record.
//
// Rules (in order):
//  1. If the active user IS this child and has an explicit `courseTemplate`,
//     trust it (after sanity-checking it against the known set).
//  2. Otherwise, fall back to the studentId convention: 'anita' → 'anita',
//     'max' → 'max'. Unknown studentIds default to 'max' so legacy /lab/max
//     accounts keep working.
//  3. An unrecognised explicit courseTemplate falls back to 'max'.
const KNOWN_COURSE_TEMPLATES = ['max', 'anita'];
export const getCourseTemplateForChild = (user, childId) => {
  const explicit =
    user && user.studentId === childId ? user.courseTemplate : null;
  if (explicit && KNOWN_COURSE_TEMPLATES.includes(explicit)) return explicit;
  if (childId === 'anita') return 'anita';
  if (childId === 'max') return 'max';
  // For dynamic / future student ids without a courseTemplate hint, default
  // to Max so existing accounts keep their curriculum.
  return 'max';
};

const DEFAULT_STATE = {
  user: null, // { id, name, role: 'parent'|'child', studentId: 'max'|'anita'|null }
  language: 'ru', // Legacy saved field; rendered locale comes from LanguageContext.
  progress: {} // { [studentId]: { [`${weekId}/${dayId}`]: { status, responses, completedAt } } }
};

// Auth lives entirely on the backend now (HttpOnly cookie session).
// Frontend talks to /api/lab/auth/{login,me,logout}.
//
// We resolve the API origin defensively: if REACT_APP_BACKEND_URL is set but
// points to a different origin than the page (e.g., a preview URL baked into
// a prod build), we fall back to a same-origin relative path so cookies and
// CORS keep working on both environments without a rebuild.
const resolveApiBase = () => {
  const path = '/api/lab/auth';
  const configured = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === 'undefined') return `${configured || ''}${path}`;
  if (!configured) return path;
  try {
    const u = new URL(configured);
    if (u.origin === window.location.origin) return `${configured}${path}`;
  } catch (_e) { /* fall through to same-origin */ }
  return path;
};
const API_BASE = resolveApiBase();

const apiLogin = async (login, password) => {
  const r = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ login, password })
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || 'Login failed');
  }
  return r.json();
};

const apiMe = async () => {
  const r = await fetch(`${API_BASE}/me`, { credentials: 'include' });
  if (!r.ok) return null;
  return r.json();
};

const apiLogout = async () => {
  await fetch(`${API_BASE}/logout`, { method: 'POST', credentials: 'include' });
};

// ----------------- Backend progress + Kopilka API ------------------
// New child accounts (non-legacy) read/write progress + nims through the
// backend. Same-origin fall-through resolver as for /lab/auth.
const resolveStudentApiBase = () => {
  const path = '/api/lab/students';
  const configured = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === 'undefined') return `${configured || ''}${path}`;
  if (!configured) return path;
  try {
    const u = new URL(configured);
    if (u.origin === window.location.origin) return `${configured}${path}`;
  } catch (_e) { /* same-origin fall-through */ }
  return path;
};
const STUDENT_API_BASE = resolveStudentApiBase();

const apiGetStudentState = async (sid) => {
  const r = await fetch(`${STUDENT_API_BASE}/${encodeURIComponent(sid)}/state`, {
    credentials: 'include'
  });
  if (!r.ok) return null;
  return r.json();
};

const apiCompleteLesson = async (sid, payload) => {
  const r = await fetch(`${STUDENT_API_BASE}/${encodeURIComponent(sid)}/progress/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload)
  });
  if (!r.ok) return null;
  return r.json();
};

const apiAwardBonus = async (sid, payload) => {
  const r = await fetch(`${STUDENT_API_BASE}/${encodeURIComponent(sid)}/nims/award-bonus`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload)
  });
  if (!r.ok) return null;
  return r.json();
};

const apiResetLesson = async (sid, payload) => {
  const r = await fetch(`${STUDENT_API_BASE}/${encodeURIComponent(sid)}/progress/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload)
  });
  if (!r.ok) return null;
  return r.json();
};

const apiMigrateLocalState = async (sid, payload) => {
  const r = await fetch(`${STUDENT_API_BASE}/${encodeURIComponent(sid)}/migrate-local-state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload)
  });
  if (!r.ok) return null;
  return r.json();
};

// localStorage flag set after a successful migration for a given student.
const MIGRATION_FLAG = (sid) => `nimnyLab:backendMigrated:${sid}:v1`;

// Build the migration payload from existing localStorage state for a given
// student. Returns null when there is nothing to migrate (no legacy data).
//
// Note on amounts: this reads from `nimState[sid].history`, which may have
// been augmented by the legacy reconciliation effect in this provider —
// that effect backfills missing `lesson_base` rows from `lesson.reward.base`
// (the curriculum's source of truth) when a child has progress recorded
// without a corresponding nim award. The migrated balance therefore
// reflects curriculum-true amounts, not necessarily the historical
// localStorage entries verbatim. This is intentional: it heals stale
// localStorage and gives the future Prize Shop a consistent baseline.
const buildLegacyMigrationPayload = (sid, baseState, baseNimState, courseTemplate) => {
  const progressMap = (baseState && baseState.progress && baseState.progress[sid]) || {};
  const nimRecord = (baseNimState && baseNimState[sid]) || null;

  const completedLessons = Object.entries(progressMap)
    .filter(([, rec]) => rec && rec.status === 'completed')
    .map(([key, rec]) => {
      const [weekId, dayId] = key.split('/');
      // Legacy lessonId convention: `${studentId}-${weekId}-${dayId}`
      const lessonId = `${sid}-${weekId}-${dayId}`;
      return {
        weekId: weekId || '',
        dayId: dayId || '',
        lessonId,
        completedAt: rec.completedAt || null,
        notebookDone: !!rec.notebookDone
      };
    });

  const nimTransactions = [];
  if (nimRecord && Array.isArray(nimRecord.history)) {
    for (const h of nimRecord.history) {
      if (!h || !h.lessonId || typeof h.amount !== 'number' || h.amount <= 0) continue;
      const t = h.type === 'base' ? 'lesson_base'
              : h.type === 'bonus' ? 'parent_bonus'
              : null;
      if (!t) continue;
      // Legacy bonus rows did not always carry the bonusId; reconstruct
      // from awardsByLesson when available.
      let bonusId = h.bonusId || null;
      if (t === 'parent_bonus' && !bonusId) {
        const award = (nimRecord.awardsByLesson || {})[h.lessonId];
        if (award && Array.isArray(award.bonusIds) && award.bonusIds.length) {
          // Fall back to the first bonus id; the backend's unique partial
          // index is on (studentId, lessonId, bonusId), so we still need a
          // unique value per bonus. Skip when we cannot disambiguate.
          bonusId = award.bonusIds[0];
        }
      }
      if (t === 'parent_bonus' && !bonusId) continue;
      // Try to recover weekId/dayId from the legacy lessonId.
      const m = /^([^-]+)-(week-\d+)-(day-\d+)$/.exec(h.lessonId);
      const weekId = m ? m[2] : '';
      const dayId = m ? m[3] : '';
      nimTransactions.push({
        type: t,
        lessonId: h.lessonId,
        weekId,
        dayId,
        bonusId,
        amount: Number(h.amount) || 0,
        label: h.label || h.lessonTitle || '',
        createdAt: h.createdAt || null
      });
    }
  }

  if (completedLessons.length === 0 && nimTransactions.length === 0) return null;
  return {
    courseTemplate: courseTemplate || 'max',
    completedLessons,
    nimTransactions,
    legacyBalance: nimRecord ? Number(nimRecord.balance) || 0 : 0
  };
};

// Convert backend progress[] into the local-state map keyed by `${weekId}/${dayId}`.
const backendProgressToMap = (arr) => {
  const map = {};
  for (const p of arr || []) {
    map[`${p.weekId}/${p.dayId}`] = {
      status: 'completed',
      responses: {},
      notebookDone: !!p.notebookDone,
      completedAt: p.completedAt
    };
  }
  return map;
};

// Merge a backend-hydrated progress map onto the existing local one for a
// given student. The backend is the source of truth for `status`,
// `notebookDone`, and `completedAt`, but it does NOT store per-task
// `responses` — those live only in localStorage. Without this merge, every
// hydration call (mount, login, refreshBackendStudent) would wipe the
// child's typed answers and force them to re-enter everything on every page
// reload. We:
//   • keep every local day that the backend doesn't know about (typically
//     `in_progress` days with answers but no completion yet);
//   • for days the backend reports completed, preserve the local
//     `responses` while taking the backend's status / completedAt.
const mergeProgressMaps = (localMap, remoteMap) => {
  const out = { ...(localMap || {}) };
  for (const [key, remoteEntry] of Object.entries(remoteMap || {})) {
    const localEntry = (localMap || {})[key] || {};
    out[key] = {
      ...localEntry,
      ...remoteEntry,
      responses: { ...(localEntry.responses || {}), ...(remoteEntry.responses || {}) }
    };
  }
  return out;
};

// Convert backend nim history into the awardsByLesson + history shape used
// by the existing Kopilka components, so they need no changes.
const backendNimToLocalShape = (sid, history) => {
  const safeHist = Array.isArray(history) ? history : [];
  const awardsByLesson = {};
  for (const h of safeHist) {
    const key = h.lessonId;
    const cur = awardsByLesson[key] || {
      lessonId: key,
      baseAwarded: false,
      baseAmount: 0,
      bonusIds: [],
      total: 0,
      updatedAt: null
    };
    if (h.type === 'lesson_base') {
      cur.baseAwarded = true;
      cur.baseAmount = Number(h.amount) || 0;
    } else if (h.type === 'parent_bonus' && h.bonusId) {
      if (!cur.bonusIds.includes(h.bonusId)) cur.bonusIds = [...cur.bonusIds, h.bonusId];
    }
    cur.total = (Number(cur.total) || 0) + (Number(h.amount) || 0);
    cur.updatedAt = h.createdAt;
    awardsByLesson[key] = cur;
  }
  const balance = safeHist.reduce((acc, h) => acc + (Number(h.amount) || 0), 0);
  const localHistory = safeHist.map((h) => ({
    id: h.id,
    studentId: sid,
    lessonId: h.lessonId,
    lessonTitle: h.label || h.lessonId,
    type: h.type === 'lesson_base' ? 'base' : 'bonus',
    label: h.label || '',
    amount: Number(h.amount) || 0,
    createdAt: h.createdAt
  }));
  return { balance, awardsByLesson, history: localHistory };
};

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return DEFAULT_STATE;
  }
};

const saveState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable (Safari private mode, etc.)
  }
};

// ---------- Nim rewards (Копилка Нимни) ----------
// State shape:
// {
//   [studentId]: {
//     balance: number,
//     awardsByLesson: {
//       [lessonId]: {
//         lessonId, baseAwarded, baseAmount, bonusIds: string[],
//         total, updatedAt
//       }
//     },
//     history: [
//       { id, studentId, lessonId, lessonTitle, type, label, amount, createdAt }
//     ]
//   }
// }
const DEFAULT_NIM_STATE = {};

const loadNimState = () => {
  try {
    const raw = localStorage.getItem(NIM_STORAGE_KEY);
    if (!raw) return DEFAULT_NIM_STATE;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : DEFAULT_NIM_STATE;
  } catch {
    return DEFAULT_NIM_STATE;
  }
};

const saveNimState = (nimState) => {
  try {
    localStorage.setItem(NIM_STORAGE_KEY, JSON.stringify(nimState));
  } catch {
    // ignore
  }
};

const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `nim-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

const LabContext = createContext(null);

// i18n table for interface labels. Lesson content lives in data/lessons.js.
const I18N = {
  brand: { ru: 'Nimny', en: 'Nimny' },
  labName: { ru: 'Nimny Lab', en: 'Nimny Lab' },
  labTitle: { ru: 'Мир знаний с Нимни', en: 'World of knowledge with Nimny' },
  labTagline: {
    ru: 'Тихое место, где знания открываются шаг за шагом.',
    en: 'A quiet place where knowledge unfolds step by step.'
  },
  signIn: { ru: 'Войти', en: 'Sign in' },
  loginTitle: { ru: 'Кто заходит?', en: 'Who is entering?' },
  loginAsChild: { ru: 'Войти как Макс', en: 'Sign in as Max' },
  loginAsParent: { ru: 'Войти как родитель', en: 'Sign in as parent' },
  loginHint: {
    ru: 'Это частное пространство вашей семьи.',
    en: "This is your family's private space."
  },
  // Family access code step
  familyCodeTitle: { ru: 'Введите семейный код', en: 'Enter the family code' },
  familyCodeHint: {
    ru: 'Это закрытое место вашей семьи. Код нужен, чтобы сохранить путь Макса в безопасности.',
    en: "This is your family's private space. The code keeps Max's path safe."
  },
  familyCodeLabel: { ru: 'Семейный код', en: 'Family code' },
  familyCodePlaceholder: { ru: '', en: '' },
  familyCodeContinue: { ru: 'Продолжить', en: 'Continue' },
  familyCodeWrong: {
    ru: 'Код не подошёл. Попробуйте ещё раз.',
    en: "That code didn't match. Please try again."
  },
  familyCodeSecurityNote: {
    ru: 'Доступ только у вашей семьи. Прогресс сохраняется безопасно.',
    en: 'Only your family has access. Progress is saved safely.'
  },
  // Parent PIN step
  parentPinTitle: { ru: 'PIN родителя', en: 'Parent PIN' },
  parentPinHint: {
    ru: 'Чтобы войти как родитель, введите PIN.',
    en: 'To enter as parent, please enter the PIN.'
  },
  parentPinPlaceholder: { ru: '4 цифры', en: '4 digits' },
  parentPinWrong: {
    ru: 'Неверный PIN. Попробуйте ещё раз.',
    en: 'Wrong PIN. Please try again.'
  },
  parentPinSubmit: { ru: 'Войти как родитель', en: 'Sign in as parent' },
  // Parent room (child view)
  parentRoomChildBlockTitle: {
    ru: 'Эта комната только для родителя.',
    en: 'This room is for a parent only.'
  },
  parentRoomChildBlockHint: {
    ru: 'Попроси родителя ввести PIN.',
    en: 'Ask a parent to enter the PIN.'
  },
  callParent: { ru: 'Позвать родителя', en: 'Call parent' },
  notNow: { ru: 'Не сейчас', en: 'Not now' },
  privateBadge: { ru: 'Приватно', en: 'Private' },
  privateFamily: {
    ru: 'Частное пространство семьи',
    en: 'Private family space'
  },
  privateFamilyDesc: {
    ru: 'Доступ только у вашей семьи. Все данные и прогресс надёжно защищены.',
    en: 'Only your family has access. All data and progress are protected.'
  },
  // Drawer / menu
  menuHome: { ru: 'Главная', en: 'Home' },
  menuTodayMission: { ru: 'Сегодняшняя миссия', en: "Today's mission" },
  menuMyPath: { ru: 'Мой путь', en: 'My path' },
  menuMyProjects: { ru: 'Мои проекты', en: 'My projects' },
  menuWordChest: { ru: 'Сундук слов', en: 'Word chest' },
  menuParentRoom: { ru: 'Комната родителя', en: 'Parent room' },
  menuParentUsers: { ru: 'Дети и аккаунты', en: 'Students & Accounts' },
  menuPrizeRequests: { ru: 'Запросы детей', en: "Children's requests" },
  menuPrizeShop: { ru: 'Лавка Нимни', en: 'Nimny Prize Shop' },
  menuSettings: { ru: 'Настройки', en: 'Settings' },
  menuLanguage: { ru: 'RU / EN', en: 'RU / EN' },
  menuLogout: { ru: 'Выйти', en: 'Sign out' },
  menuPersonalAccount: { ru: 'Личный аккаунт', en: 'Personal account' },
  menuProgressSavedOnly: {
    ru: 'Твой прогресс сохраняется только у тебя.',
    en: 'Your progress is saved only on your device.'
  },
  // Child home / mission
  todayLanternOpen: { ru: 'Сегодня открыт новый огонёк', en: 'A new lantern lights up today' },
  startOnline: { ru: 'Начать онлайн', en: 'Start online' },
  workInNotebook: { ru: 'Работать в тетради', en: 'Work in notebook' },
  print: { ru: 'Распечатать', en: 'Print' },
  parentRoomCard: { ru: 'Комната родителя', en: 'Parent room' },
  parentRoomDesc: {
    ru: 'Доступ для родителей и настройка обучения.',
    en: 'Access for parents and learning setup.'
  },
  nimnyLightsTheWay: {
    ru: 'освещает путь к новому открытию.',
    en: 'lights the way to a new discovery.'
  },
  // My path
  pathTitle: { ru: 'Путь Макса', en: "Max's path" },
  pathTitleAnita: { ru: 'Путь Аниты', en: "Anita's path" },
  pathTitleGeneric: { ru: 'Путь ученика', en: "Student's path" },
  pathSubtitle: {
    ru: 'Твой путь знаний — шаг за шагом.',
    en: 'Your path of knowledge — step by step.'
  },
  statusOpen: { ru: 'Открыто', en: 'Open' },
  statusInProgress: { ru: 'В процессе', en: 'In progress' },
  statusCompleted: { ru: 'Завершено', en: 'Completed' },
  statusLocked: { ru: 'Закрыто', en: 'Locked' },
  weekLabel: { ru: 'Неделя', en: 'Week' },
  dayLabel: { ru: 'Day', en: 'Day' },
  goingStepByStep: { ru: 'Идём шаг за шагом', en: 'We go step by step' },
  goingStepByStepDesc: {
    ru: 'Новые разделы откроются, когда ты будешь готов. Спешить не нужно — важно понимать и пробовать.',
    en: "New sections open when you're ready. No rush — what matters is understanding and trying."
  },
  // Lesson
  lessonIntro: { ru: 'Привет от Нимни', en: 'A hello from Nimny' },
  goalsLabel: { ru: 'Цели урока', en: 'Lesson goals' },
  vocabularyLabel: { ru: 'Сундук слов', en: 'Word chest' },
  sentencesLabel: { ru: 'предложений', en: 'sentences' },
  ifEasyLabel: { ru: 'Если легко', en: 'If easy' },
  ifDifficultLabel: { ru: 'Если трудно', en: 'If difficult' },
  lessonOnlineMode: { ru: 'Онлайн-задания', en: 'Online tasks' },
  lessonNotebookMode: { ru: 'В тетради', en: 'In the notebook' },
  lessonPrintMode: { ru: 'Распечатать', en: 'Print' },
  lessonParentMode: { ru: 'Комната родителя', en: 'Parent room' },
  lessonComplete: { ru: 'Я завершил день', en: 'I have completed the day' },
  lessonAlreadyComplete: { ru: 'День завершён', en: 'Day completed' },
  notebookDoneToggle: { ru: 'Я сделал работу в тетради', en: 'I finished my notebook work' },
  yourAnswer: { ru: 'Твой ответ', en: 'Your answer' },
  answerPlaceholder: {
    ru: 'Напиши своими словами…',
    en: 'Write in your own words…'
  },
  hint: { ru: 'Подсказка', en: 'Hint' },
  back: { ru: 'Назад', en: 'Back' },
  saved: { ru: 'Сохранено', en: 'Saved' },
  // Parent guide
  parentGuideGoal: { ru: 'Цель урока', en: 'Lesson goal' },
  parentGuideCheck: { ru: 'Что проверить', en: 'What to check' },
  parentGuideAnswers: { ru: 'Ключ ответов', en: 'Answer key' },
  parentGuidePrompts: { ru: 'Темы для разговора', en: 'Discussion prompts' },
  // Parent dashboard
  parentDashTitle: { ru: 'Кабинет родителя', en: 'Parent dashboard' },
  parentDashSub: { ru: 'Прогресс Макса и материалы для печати.', en: "Max's progress and printable materials." },
  childProgress: { ru: 'Прогресс ребёнка', en: 'Child progress' },
  printToday: { ru: 'Распечатать сегодняшний день', en: "Print today's worksheet" },
  parentGuideAction: { ru: 'Открыть гид родителя', en: 'Open parent guide' },
  reviewLesson: { ru: 'Посмотреть урок', en: 'Review lesson' },
  unlockOverride: { ru: 'Открыть следующий день (override)', en: 'Unlock next day (override)' },
  unlockOverrideDone: { ru: 'Следующий день открыт', en: 'Next day unlocked' },
  // Print worksheet
  printSheetTitle: { ru: 'Рабочий лист', en: 'Worksheet' },
  printNamePrompt: { ru: 'Имя:', en: 'Name:' },
  printDatePrompt: { ru: 'Дата:', en: 'Date:' },
  printAction: { ru: 'Печать', en: 'Print' },
  // Копилка Нимни (kopilka)
  kopilkaTitle: { ru: 'Копилка Нимни', en: 'Копилка Нимни' },
  kopilkaIntro: {
    ru: 'Нимы — это призовые токены курса. Они показывают твой прогресс.',
    en: 'Нимы — это призовые токены курса. Они показывают твой прогресс.'
  },
  kopilkaBalanceLabel: { ru: 'В копилке:', en: 'В копилке:' },
  kopilkaEmpty: {
    ru: 'Копилка пока пустая. Заверши первый учебный день, чтобы получить первую награду.',
    en: 'Копилка пока пустая. Заверши первый учебный день, чтобы получить первую награду.'
  },
  kopilkaHistoryTitle: { ru: 'История наград', en: 'История наград' },
  kopilkaOpen: { ru: 'Открыть копилку', en: 'Открыть копилку' },
  kopilkaCardSubtitle: { ru: 'Призовые токены курса', en: 'Призовые токены курса' },
  rewardOfTheDay: { ru: 'Награда дня', en: 'Награда дня' },
  rewardForCompletion: { ru: 'За завершение', en: 'За завершение' },
  rewardForProject: { ru: 'За проект', en: 'За проект' },
  rewardBonusUpTo: { ru: 'Бонусы: до', en: 'Бонусы: до' },
  rewardAlreadyAwarded: {
    ru: 'Награда за завершение уже начислена.',
    en: 'Награда за завершение уже начислена.'
  },
  parentBonusTitle: { ru: 'Бонусы к награде', en: 'Бонусы к награде' },
  parentBonusHint: {
    ru: 'Бонусы можно начислить после проверки тетради, устного ответа или проекта.',
    en: 'Бонусы можно начислить после проверки тетради, устного ответа или проекта.'
  },
  parentBonusAdd: { ru: 'Начислить', en: 'Начислить' },
  parentBonusAlready: { ru: 'Уже начислено', en: 'Уже начислено' },
  // Week 10 quest map + Nimny Night Runner Lite
  questMapTitle: { ru: 'Большая миссия Нимни', en: 'Nimny Final Quest' },
  questMapSubtitle: {
    ru: 'Собери огоньки знаний и открой ночную игру',
    en: 'Collect the lights of knowledge and unlock the night game'
  },
  questMapLockedTitle: { ru: 'Карта пока спит', en: 'The map is still sleeping' },
  questMapLockedHint: {
    ru: 'Большая миссия откроется после Недели 9.',
    en: 'The Final Quest opens after Week 9.'
  },
  questNodeMoney: { ru: 'Огонёк денег', en: 'Money Light' },
  questNodeBank: { ru: 'Огонёк банка', en: 'Bank Light' },
  questNodeWorld: { ru: 'Огонёк мира', en: 'World Light' },
  questNodeIdeas: { ru: 'Огонёк идей', en: 'Ideas Light' },
  questNodeFinalGate: { ru: 'Финальные ворота', en: 'Final Gate' },
  questPortalLabel: { ru: 'Ночная игра', en: 'Night Game' },
  questPortalLockedHint: {
    ru: 'Откроется после Финальных ворот',
    en: 'Unlocks after the Final Gate'
  },
  questPortalUnlockedHint: {
    ru: 'Нимни приготовила для тебя ночную игру',
    en: 'Nimny has prepared the night game for you'
  },
  questOpenMap: { ru: 'Открыть карту', en: 'Open quest map' },
  questOpenNightGame: { ru: 'Открыть ночную игру', en: 'Open night game' },
  questNimnyHere: { ru: 'Нимни здесь', en: 'Nimny is here' },
  // Night Runner game
  nightRunnerTitle: { ru: 'Ночная игра Нимни', en: 'Nimny Night Runner Lite' },
  nightRunnerLockedTitle: { ru: 'Игра пока закрыта', en: 'The game is still closed' },
  nightRunnerLockedHint: {
    ru: 'Ночная игра откроется после завершения Финальных ворот.',
    en: 'The night game unlocks after completing the Final Gate.'
  },
  nightRunnerReadyHint: {
    ru: 'Нимни открыла ночную игру. Собирай звёздочки и перепрыгивай мягкие препятствия.',
    en: 'Nimny unlocked the night game. Collect stars and jump over soft obstacles.'
  },
  nightRunnerStart: { ru: 'Начать пробежку', en: 'Start run' },
  nightRunnerJump: { ru: 'Прыжок', en: 'Jump' },
  nightRunnerJumpHint: {
    ru: 'Пробел или нажатие на экран — прыжок.',
    en: 'Press Space or tap the screen to jump.'
  },
  nightRunnerScore: { ru: 'Звёздочки', en: 'Stars' },
  nightRunnerBest: { ru: 'Лучший счёт', en: 'Best' },
  nightRunnerObjective: { ru: 'Собери 20 звёздочек', en: 'Collect 20 stars' },
  nightRunnerStreak: { ru: 'Огонёк!', en: 'Streak!' },
  nightRunnerReady: {
    ru: 'Нимни готова к ночной пробежке',
    en: 'Nimny is ready for a night run'
  },
  nightRunnerPlay: { ru: 'Играть', en: 'Play' },
  nightRunnerTryAgain: { ru: 'Попробуй ещё раз', en: 'Try again' },
  nightRunnerGameOver: { ru: 'Тихая остановка', en: 'Soft stop' },
  nightRunnerGameOverHint: {
    ru: 'Нимни остановилась. Можно отдохнуть и попробовать ещё раз.',
    en: 'Nimny paused. Take a breath and try again.'
  },
  nightRunnerRestart: { ru: 'Ещё раз', en: 'Try again' },
  nightRunnerBack: { ru: 'Вернуться', en: 'Back' }
};

export const LabProvider = ({ children }) => {
  const { language: siteLanguage, setLanguage: setSiteLanguage } = useLanguage();
  // Only the locale is shared with the public site. Existing Lab account,
  // progress and wallet state stays isolated. Legacy state.language is not
  // authoritative; Lab has EN/RU translations only (ES falls back to EN).
  const language = siteLanguage === 'ru' ? 'ru' : 'en';
  const [state, setState] = useState(loadState);
  const [nimState, setNimState] = useState(loadNimState);
  // True until the initial /api/lab/auth/me round-trip resolves. Route
  // guards must wait on this so a direct navigation to a protected URL
  // does not bounce the user to /lab/login (and lose the original path)
  // before the session cookie is exchanged for the user payload.
  const [authLoading, setAuthLoading] = useState(true);

  // Run a one-time idempotent migration of legacy localStorage state into
  // the backend ledger for the given backend-mode user. The unique indexes
  // in `lab_progress` and `lab_nim_tx` keep this safe even if the flag
  // disappears or another browser already ran the migration.
  const migrateLegacyIfNeeded = async (u, baseStateSnapshot, baseNimSnapshot) => {
    if (!u || !isBackendStudent(u)) return false;
    const sid = u.studentId;
    try {
      if (typeof window === 'undefined') return false;
      if (window.localStorage.getItem(MIGRATION_FLAG(sid)) === 'true') return false;
    } catch (_e) { /* localStorage unavailable */ }

    const payload = buildLegacyMigrationPayload(
      sid,
      baseStateSnapshot,
      baseNimSnapshot,
      u.courseTemplate || 'max'
    );
    if (!payload) {
      // Nothing to migrate — still set the flag so we don't probe again.
      try { window.localStorage.setItem(MIGRATION_FLAG(sid), 'true'); } catch (_e) {}
      return false;
    }
    const result = await apiMigrateLocalState(sid, payload);
    if (!result) return false;
    try { window.localStorage.setItem(MIGRATION_FLAG(sid), 'true'); } catch (_e) {}
    return true;
  };

  // On mount, ask backend who is logged in. If the cookie is valid we hydrate
  // state.user from the server; otherwise we drop any stale local user.
  // For backend-mode children we ALSO migrate legacy localStorage state on
  // first run, then hydrate progress + nims from backend.
  useEffect(() => {
    let alive = true;
    (async () => {
      const u = await apiMe();
      if (!alive) return;
      setState((s) => ({ ...s, user: u }));
      setAuthLoading(false);
      if (!u || !isBackendStudent(u)) return;
      // Snapshot legacy state BEFORE backend hydration overwrites it.
      const baseStateSnapshot = loadState();
      const baseNimSnapshot = loadNimState();
      await migrateLegacyIfNeeded(u, baseStateSnapshot, baseNimSnapshot);
      if (!alive) return;
      const remote = await apiGetStudentState(u.studentId);
      if (!alive || !remote) return;
      const sid = u.studentId;
      const progressMap = backendProgressToMap(remote.progress);
      const nimRecord = backendNimToLocalShape(sid, remote.nims?.history);
      setState((s) => ({
        ...s,
        progress: {
          ...s.progress,
          [sid]: mergeProgressMaps(s.progress?.[sid], progressMap)
        }
      }));
      setNimState((prev) => ({ ...(prev || {}), [sid]: nimRecord }));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveState(state);
  }, [state]);

  useEffect(() => {
    saveNimState(nimState);
  }, [nimState]);

  // Reconciliation: any lesson already marked completed in legacy progress
  // but missing a base nim award gets one base award (one-shot per lesson).
  // Runs whenever progress changes — applyAward is idempotent when the base
  // is already awarded, so duplicate runs are safe.
  // SKIPPED for backend-mode children — their nims are computed server-side.
  useEffect(() => {
    if (isBackendStudent(state.user)) return;
    const progressByChild = state.progress || {};
    const childIds = Object.keys(progressByChild);
    if (childIds.length === 0) return;

    let touched = false;
    const nextNim = { ...(nimState || {}) };

    for (const childId of childIds) {
      const progress = progressByChild[childId] || {};
      const studentNim =
        nextNim[childId] || { balance: 0, awardsByLesson: {}, history: [] };
      let working = { ...studentNim, _studentId: childId };
      let changed = false;

      for (const key of Object.keys(progress)) {
        const rec = progress[key];
        if (!rec || rec.status !== 'completed') continue;
        const [weekId, dayId] = key.split('/');
        const childCourse = getCourseTemplateForChild(state.user, childId);
        const day = findDay(weekId, dayId, getWeeksForCourse(childCourse));
        if (!day || !day.reward) continue;
        const lessonKey = buildLessonKey(childId, weekId, dayId);
        if (working.awardsByLesson?.[lessonKey]?.baseAwarded) continue;
        const updated = applyAward(
          working,
          lessonKey,
          day.reward,
          // tx() not yet bound (scope), so use language preference directly
          (day.title && (day.title[language] || day.title.en || day.title.ru)) ||
            lessonKey,
          'base'
        );
        if (updated !== working) {
          working = updated;
          changed = true;
        }
      }

      if (changed) {
        const { _studentId, ...clean } = working;
        nextNim[childId] = clean;
        touched = true;
      }
    }

    if (touched) setNimState(nextNim);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.progress]);

  const t = (key) => {
    const entry = I18N[key];
    if (!entry) return key;
    return entry[language] || entry.en || key;
  };

  const tx = (obj) => {
    if (!obj) return '';
    return obj[language] || obj.en || obj.ru || '';
  };

  const setLanguage = (lang) => setSiteLanguage(lang === 'ru' ? 'ru' : 'en');
  const toggleLanguage = () => setLanguage(language === 'ru' ? 'en' : 'ru');

  const signOut = async () => {
    try { await apiLogout(); } catch { /* ignore network errors */ }
    setState((s) => ({ ...s, user: null }));
  };

  // Login: server-verified password against backend session cookie. After a
  // successful login for a backend-mode (non-legacy) child we eagerly run
  // any pending legacy migration and hydrate progress + nims from the
  // server so the next render has everything.
  const login = async (loginId, password) => {
    const u = await apiLogin(loginId, password);
    setState((s) => ({ ...s, user: u }));
    if (isBackendStudent(u)) {
      // Snapshot the freshest state before hydration overwrites it.
      const baseStateSnapshot = loadState();
      const baseNimSnapshot = loadNimState();
      await migrateLegacyIfNeeded(u, baseStateSnapshot, baseNimSnapshot);
      const remote = await apiGetStudentState(u.studentId);
      if (remote) {
        const sid = u.studentId;
        const progressMap = backendProgressToMap(remote.progress);
        const nimRecord = backendNimToLocalShape(sid, remote.nims?.history);
        setState((s) => ({
          ...s,
          progress: {
            ...s.progress,
            [sid]: mergeProgressMaps(s.progress?.[sid], progressMap)
          }
        }));
        setNimState((prev) => ({ ...(prev || {}), [sid]: nimRecord }));
      }
    }
    return u;
  };

  // Refresh a student's progress + nim state from the backend (used after
  // every backend-mode complete/bonus call so the UI shows the new state).
  const refreshBackendStudent = async (sid) => {
    if (!sid) return;
    const remote = await apiGetStudentState(sid);
    if (!remote) return;
    const progressMap = backendProgressToMap(remote.progress);
    const nimRecord = backendNimToLocalShape(sid, remote.nims?.history);
    setState((s) => ({
      ...s,
      progress: {
        ...s.progress,
        [sid]: mergeProgressMaps(s.progress?.[sid], progressMap)
      }
    }));
    setNimState((prev) => ({ ...(prev || {}), [sid]: nimRecord }));
  };

  // Progress helpers
  const childProgress = (childId) => state.progress?.[childId] || {};

  const updateLessonResponse = (childId, weekId, dayId, responseId, value) => {
    setState((s) => {
      const childProg = s.progress?.[childId] || {};
      const key = `${weekId}/${dayId}`;
      const existing = childProg[key] || {
        status: 'in_progress',
        responses: {},
        notebookDone: false,
        completedAt: null
      };
      return {
        ...s,
        progress: {
          ...s.progress,
          [childId]: {
            ...childProg,
            [key]: {
              ...existing,
              status: existing.status === 'completed' ? 'completed' : 'in_progress',
              responses: { ...existing.responses, [responseId]: value }
            }
          }
        }
      };
    });
  };

  const setNotebookDone = (childId, weekId, dayId, done) => {
    setState((s) => {
      const childProg = s.progress?.[childId] || {};
      const key = `${weekId}/${dayId}`;
      const existing = childProg[key] || {
        status: 'in_progress',
        responses: {},
        notebookDone: false,
        completedAt: null
      };
      return {
        ...s,
        progress: {
          ...s.progress,
          [childId]: {
            ...childProg,
            [key]: {
              ...existing,
              status: existing.status === 'completed' ? 'completed' : 'in_progress',
              notebookDone: done
            }
          }
        }
      };
    });
  };

  const completeLesson = (childId, weekId, dayId) => {
    // Backend write-through. Required for BOTH:
    //   (a) the child completing their own day, AND
    //   (b) the parent/super_admin completing a day for a child in the family
    //       (because the child's «Я завершил день» button was removed — the
    //       parent's button is now the only path that creates the backend
    //       progress row + base nim transaction).
    const callerRole = state.user?.role;
    const isParentLike = callerRole === 'parent' || callerRole === 'super_admin';
    const isSelfChild = isBackendStudent(state.user) && state.user.studentId === childId;
    if (isSelfChild || isParentLike) {
      const courseTemplate = getCourseTemplateForChild(state.user, childId);
      const day = findDay(weekId, dayId, getWeeksForCourse(courseTemplate));
      const reward = (day && day.reward) || { base: 0, label: '' };
      const lessonId = buildLessonKey(childId, weekId, dayId);
      const lessonTitleObj = (day && day.title) || {};
      const label =
        reward.label ||
        lessonTitleObj[language] ||
        lessonTitleObj.en ||
        lessonTitleObj.ru ||
        'За завершение';
      const notebookDone = !!(state.progress?.[childId]?.[`${weekId}/${dayId}`]?.notebookDone);
      return (async () => {
        const resp = await apiCompleteLesson(childId, {
          weekId,
          dayId,
          lessonId,
          courseTemplate,
          baseAmount: Number(reward.base) || 0,
          rewardLabel: label,
          notebookDone
        });
        if (!resp) return null;
        await refreshBackendStudent(childId);
        return resp;
      })();
    }
    setState((s) => {
      const childProg = s.progress?.[childId] || {};
      const key = `${weekId}/${dayId}`;
      const existing = childProg[key] || { responses: {}, notebookDone: false };
      return {
        ...s,
        progress: {
          ...s.progress,
          [childId]: {
            ...childProg,
            [key]: {
              ...existing,
              status: 'completed',
              completedAt: new Date().toISOString()
            }
          }
        }
      };
    });
  };

  // Parent override: mark a day as completed without going through lesson UI
  // (used by "unlock override" on parent dashboard).
  const overrideUnlockNext = (childId, weekId, dayId) => {
    completeLesson(childId, weekId, dayId);
  };

  // Parent-only: undo a day completion and revoke its base nim award.
  // Returns a promise that resolves to the parsed backend response (or null
  // on failure). Local state is refreshed on success so the UI flips back
  // to "in_progress" and the parent sees the new balance immediately.
  const resetLesson = async (childId, weekId, dayId) => {
    if (!childId) return null;
    const courseTemplate = getCourseTemplateForChild(state.user, childId);
    const lessonId = buildLessonKey(childId, weekId, dayId);
    const resp = await apiResetLesson(childId, {
      weekId,
      dayId,
      lessonId,
      courseTemplate
    });
    if (!resp) return null;
    // Mirror change locally: drop completion + base award from the in-memory
    // state so the next render sees "in_progress" without waiting for a
    // round-trip refresh. Then refresh from the backend to stay canonical.
    setState((s) => {
      const childProg = { ...(s.progress?.[childId] || {}) };
      const key = `${weekId}/${dayId}`;
      if (childProg[key]) {
        const { completedAt: _ca, ...rest } = childProg[key];
        childProg[key] = { ...rest, status: 'in_progress' };
      }
      return { ...s, progress: { ...s.progress, [childId]: childProg } };
    });
    await refreshBackendStudent(childId);
    return resp;
  };

  // ---------- Копилка Нимни (nim rewards) ----------
  // Pure read helpers. They fall back gracefully when no record exists.
  const getStudentNim = (studentId) =>
    nimState?.[studentId] || { balance: 0, awardsByLesson: {}, history: [] };

  const getNimBalance = (studentId) =>
    Number(getStudentNim(studentId).balance) || 0;

  const getNimHistory = (studentId) =>
    Array.isArray(getStudentNim(studentId).history)
      ? getStudentNim(studentId).history
      : [];

  const getLessonNimAward = (studentId, lessonKey) =>
    getStudentNim(studentId).awardsByLesson?.[lessonKey] || null;

  const hasBaseNimAward = (studentId, lessonKey) => {
    const rec = getLessonNimAward(studentId, lessonKey);
    return !!(rec && rec.baseAwarded);
  };

  const hasBonusNimAward = (studentId, lessonKey, bonusId) => {
    const rec = getLessonNimAward(studentId, lessonKey);
    return !!(rec && Array.isArray(rec.bonusIds) && rec.bonusIds.includes(bonusId));
  };

  // Internal: apply a reward update and return the new student record.
  const applyAward = (studentRecord, lessonKey, lessonReward, lessonTitle, type, bonusId) => {
    const reward = lessonReward || null;
    if (!reward) return studentRecord;

    const existing = studentRecord.awardsByLesson?.[lessonKey] || {
      lessonId: lessonKey,
      baseAwarded: false,
      baseAmount: 0,
      bonusIds: [],
      total: 0,
      updatedAt: null
    };

    let added = 0;
    let label = '';
    let nextRecord = { ...existing };

    if (type === 'base') {
      if (existing.baseAwarded) return studentRecord;
      const baseAmount = Number(reward.base) || 0;
      // The cap below also defends against a base value larger than max.
      const newTotal = Math.min(
        baseAmount + (existing.bonusIds?.length || 0),
        Number(reward.max) || baseAmount
      );
      added = newTotal - (Number(existing.total) || 0);
      if (added <= 0) return studentRecord;
      label = reward.label || 'Награда за завершение';
      nextRecord = {
        ...existing,
        baseAwarded: true,
        baseAmount,
        total: newTotal,
        updatedAt: new Date().toISOString()
      };
    } else if (type === 'bonus') {
      const bonusDef = (reward.bonuses || []).find((b) => b.id === bonusId);
      if (!bonusDef) return studentRecord;
      if (existing.bonusIds?.includes(bonusId)) return studentRecord;
      // Recalculate total with the new bonus, capped at lesson max.
      const newBonusIds = [...(existing.bonusIds || []), bonusId];
      const newTotal = calculateLessonRewardTotal(reward, newBonusIds);
      added = newTotal - (Number(existing.total) || 0);
      if (added <= 0) return studentRecord; // already at cap
      label = bonusDef.label || 'Бонус';
      nextRecord = {
        ...existing,
        bonusIds: newBonusIds,
        total: newTotal,
        updatedAt: new Date().toISOString()
      };
    } else {
      return studentRecord;
    }

    const newBalance = (Number(studentRecord.balance) || 0) + added;
    const newHistory = [
      {
        id: newId(),
        studentId: studentRecord._studentId,
        lessonId: lessonKey,
        lessonTitle: lessonTitle || lessonKey,
        type,
        label,
        amount: added,
        createdAt: new Date().toISOString()
      },
      ...(studentRecord.history || [])
    ];

    return {
      ...studentRecord,
      balance: newBalance,
      awardsByLesson: {
        ...(studentRecord.awardsByLesson || {}),
        [lessonKey]: nextRecord
      },
      history: newHistory
    };
  };

  const awardBaseNimsForLesson = (studentId, lesson) => {
    if (!studentId || !lesson) return;
    // Backend-mode children get their base nim awarded server-side as part
    // of completeLesson(); double-booking the local store would skew the
    // display until the next hydration. Skip.
    if (state.user && isBackendStudent(state.user) && state.user.studentId === studentId) {
      return;
    }
    const lessonKey =
      lesson.id ||
      (lesson.weekId && lesson.dayId
        ? buildLessonKey(studentId, lesson.weekId, lesson.dayId)
        : null);
    if (!lessonKey) return;
    const reward = lesson.reward;
    const lessonTitle = lesson.title || '';
    setNimState((prev) => {
      const cur = prev?.[studentId] || { balance: 0, awardsByLesson: {}, history: [] };
      const withId = { ...cur, _studentId: studentId };
      const next = applyAward(withId, lessonKey, reward, lessonTitle, 'base');
      const { _studentId, ...clean } = next;
      return { ...(prev || {}), [studentId]: clean };
    });
  };

  const awardBonusNimForLesson = (studentId, lesson, bonusId) => {
    if (!studentId || !lesson || !bonusId) return;
    // All students (including legacy 'max'/'anita') now live in MongoDB, so
    // a parent always writes the bonus through to the backend. Failed calls
    // surface in the console; the local nim state stays untouched so the
    // button reverts to its "available" state on next hydration.
    if (state.user && (state.user.role === 'parent' || state.user.role === 'super_admin')) {
      const reward = lesson.reward || {};
      const bonusDef = (reward.bonuses || []).find((b) => b.id === bonusId);
      if (!bonusDef) return;
      const lessonTitleObj = lesson.title || {};
      const label =
        bonusDef.label ||
        lessonTitleObj[language] ||
        lessonTitleObj.en ||
        lessonTitleObj.ru ||
        'Бонус';
      const courseTemplate = getCourseTemplateForChild(state.user, studentId);
      return (async () => {
        const result = await apiAwardBonus(studentId, {
          weekId: lesson.weekId,
          dayId: lesson.dayId,
          lessonId:
            lesson.id ||
            (lesson.weekId && lesson.dayId
              ? buildLessonKey(studentId, lesson.weekId, lesson.dayId)
              : ''),
          courseTemplate,
          bonusId,
          amount: Number(bonusDef.amount) || 0,
          label
        });
        if (!result) {
          // Backend rejected the award (e.g. not authorized). Do NOT mutate
          // local state — that would falsely show the bonus as granted.
          // eslint-disable-next-line no-console
          console.warn('[lab] award-bonus failed', { studentId, bonusId });
          return;
        }
        await refreshBackendStudent(studentId);
      })();
    }
    const lessonKey =
      lesson.id ||
      (lesson.weekId && lesson.dayId
        ? buildLessonKey(studentId, lesson.weekId, lesson.dayId)
        : null);
    if (!lessonKey) return;
    const reward = lesson.reward;
    const lessonTitle = lesson.title || '';
    setNimState((prev) => {
      const cur = prev?.[studentId] || { balance: 0, awardsByLesson: {}, history: [] };
      const withId = { ...cur, _studentId: studentId };
      const next = applyAward(withId, lessonKey, reward, lessonTitle, 'bonus', bonusId);
      const { _studentId, ...clean } = next;
      return { ...(prev || {}), [studentId]: clean };
    });
  };

  const value = useMemo(
    () => ({
      state: { ...state, language },
      t,
      tx,
      language,
      setLanguage,
      toggleLanguage,
      user: state.user,
      authLoading,
      login,
      signOut,
      childProgress,
      updateLessonResponse,
      setNotebookDone,
      completeLesson,
      overrideUnlockNext,
      resetLesson,
      refreshBackendStudent,
      // Копилка Нимни
      nimState,
      getNimBalance,
      getNimHistory,
      getLessonNimAward,
      hasBaseNimAward,
      hasBonusNimAward,
      awardBaseNimsForLesson,
      awardBonusNimForLesson,
      formatNims
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, nimState, authLoading, language, setSiteLanguage]
  );

  return <LabContext.Provider value={value}>{children}</LabContext.Provider>;
};

export const useLab = () => {
  const ctx = useContext(LabContext);
  if (!ctx) throw new Error('useLab must be used within <LabProvider>');
  return ctx;
};
